import "./styles.scss";
import "moment/locale/pt";

import { API, Endpoints } from "utils/api";
import { Column, Pie } from "@ant-design/plots";
import { ConfigProvider, DatePicker, Drawer, Progress, Select, notification } from "antd";
import {
	buildColumnConfig,
	buildConsultationsSeries,
	buildDiagnosisSeries,
	buildPieConfig,
	withPercentages
} from "./chartConfigs";
import Strings, { renderString } from "utils/strings";
import { downloadBlob, filenameFromDisposition, messageFromJsonBlob } from "utils/download";
import { setBreadcrumb, setLoader } from "store/actions";

import { DateTime } from "luxon";
import { Footer } from "screens";
import { Helmet } from "react-helmet";
import { Icons } from "components";
import React from "react";
// @ts-ignore
import Variables from "styles/variables.scss";
import { connect } from "react-redux";
import en from "antd/es/locale/en_GB";
import finantialIcon from "assets/images/icon-financeiro.svg";
import healthInsuranceIcon from "assets/images/icon-segurosaude.svg";
import legalIcon from "assets/images/icon-juridico.svg";
import moment from "moment";
import nutritionalIcon from "assets/images/icon-nutricional.svg";
import pt from "antd/es/locale/pt_PT";
import socialIcon from "assets/images/icon-social.svg";
import taxIcon from "assets/images/icon-fiscal.svg";

const { RangePicker } = DatePicker;

/**
 * Same file-name slug the API uses for the report (`team24_dashboard_<company>_<start>_<end>.pdf`); only
 * needed when the `Content-Disposition` header cannot be read.
 */
const slugify = (value?: string | null) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

class Dashboard extends React.Component<any, any> {
	constructor(props: any) {
		super(props);

		if (this.props.language === "pt") {
			moment.updateLocale("pt", {
				week: {
					dow: 1 /// Date offset
				}
			});
		} else {
			moment.updateLocale("en", {
				week: {
					dow: 1 /// Date offset
				}
			});
		}

		this.state = {
			cards: null,
			filters: {
				startDate: moment.utc().add(-7, "days"),
				endDate: moment.utc()
			},
			showDrawer: false,
			consultations: [],
			hotlineData: [],
			disorders: [],
			exporting: false
		};
	}

	componentDidMount() {
		const { dispatch } = this.props;

		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.dashboard,
						route: "/dashboard"
					}
				]
			}))
		);

		this.getData();
	}

	async getData() {
		const { filters } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriDashboard(),
				params: {
					startDate: filters.startDate.format("YYYY-MM-DD"),
					endDate: filters.endDate.format("YYYY-MM-DD"),
					group: filters.group
				}
			});

			if (response.ok) {
				const { analytics, viewDiagnosedDisorders, business } = response.data.results || {};

				const hotlineData = [] as any;
				if (analytics.supportLines) {
					hotlineData.push(
						{
							type: Strings.dashboard.calls,
							value: analytics.supportLines?.calls ?? 0
						},
						{
							type: Strings.dashboard.chats,
							value: analytics.supportLines?.chats ?? 0
						}
					);
				}

				this.setState({ ...analytics, hotlineData, viewDiagnosedDisorders, business });
			} else {
				notification.error({
					message: Strings.sidebar.dashboard,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.dashboard,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	/**
	 * Downloads the usage report of the current filters. The PDF is generated by the API
	 * (GET /statistics/business/report, same authorization as the dashboard) and received as a Blob.
	 */
	async exportPdf() {
		const { dispatch } = this.props;
		const { exporting, business, filters } = this.state;

		if (exporting) return;

		const startDate = filters.startDate.format("YYYY-MM-DD");
		const endDate = filters.endDate.format("YYYY-MM-DD");

		this.setState({ exporting: true });
		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriDashboardReport(),
				params: { startDate, endDate, group: filters.group },
				axiosConfig: { responseType: "blob" }
			});

			// With `responseType: "blob"` an error body is a Blob as well, so `response.data.message` is not
			// available here: 429 has its own text, any other error shows the API message read back from the blob.
			const blob = response?.data;
			if (response?.ok && blob instanceof Blob && /^application\/pdf\b/i.test(blob.type)) {
				const fallback = ["team24_dashboard", slugify(business?.name), startDate, endDate]
					.filter(Boolean)
					.join("_")
					.concat(".pdf");

				downloadBlob(blob, filenameFromDisposition(response.headers?.["content-disposition"], fallback));
			} else if (response?.status === 429) {
				notification.warning({
					message: Strings.sidebar.dashboard,
					description: Strings.dashboard.exportRateLimited,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.sidebar.dashboard,
					description: (await messageFromJsonBlob(blob)) || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.dashboard,
				description: Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		} finally {
			dispatch(setLoader(false));
			this.setState({ exporting: false });
		}
	}

	disabledRange = (current: any) => {
		return moment.utc().isBefore(current);
	};

	renderCards() {
		const { contractPercentage, calls, chats, users } = this.state;

		return (
			<div className="DashboardCards">
				<div className="DashboardCard">
					<h3>{Strings.dashboard.contract}</h3>
					<div className="DashboardCardGfx">
						<Progress
							type="circle"
							width={70}
							status={contractPercentage === -1 ? "exception" : "normal"}
							percent={contractPercentage}
							strokeWidth={10}
							strokeColor={Variables.primaryColor}
						/>
					</div>
					<div className="DashboardCardDetails">
						<p className="DashboardCardNumber">&nbsp;</p>
						<span onClick={() => this.setState({ showDrawer: true })}>{Strings.dashboard.details}</span>
					</div>
				</div>
				<div className="DashboardCard">
					<h3>{Strings.dashboard.sosButton}</h3>
					<div className="DashboardCardGfx">
						<div className="DashboardCardIcon --bg-orange">
							<Icons.Phone />
						</div>
					</div>
					<div className="DashboardCardDetails">
						<p className="DashboardCardNumber">{calls ?? "0"}</p>
						<p className="DashboardGrayInfo">{Strings.dashboard.clicked}</p>
					</div>
				</div>
				<div className="DashboardCard">
					<h3>{Strings.dashboard.chatButton}</h3>
					<div className="DashboardCardGfx">
						<div className="DashboardCardIcon --bg-green">
							<Icons.ChatFilled bigger />
						</div>
					</div>
					<div className="DashboardCardDetails">
						<p className="DashboardCardNumber">{chats ?? "0"}</p>
						<p className="DashboardGrayInfo">{Strings.dashboard.clicked}</p>
					</div>
				</div>
				<div className="DashboardCard">
					<h3>{Strings.dashboard.usage}</h3>
					<div className="DashboardCardGfx">
						<div className="DashboardCardIcon --bg-blue">
							<Icons.UsersFilled bigger />
						</div>
					</div>
					<div className="DashboardCardDetails">
						<p className="DashboardCardNumber">{users ?? "0"}</p>
						<p className="DashboardGrayInfo">{Strings.dashboard.newUsers}</p>
					</div>
				</div>
			</div>
		);
	}

	renderGfx() {
		const { hotlineData, diagnosis = [], consultations, viewDiagnosedDisorders } = this.state;

		const consultationsSeries = buildConsultationsSeries(consultations);
		const diagnosisSeries = buildDiagnosisSeries(diagnosis);

		return (
			<React.Fragment>
				<div className="DashboardGfxContainer">
					<div className="CardWrapper --card-50">
						<h3>{Strings.dashboard.videoAppointments}</h3>
						<Column className="videoConsultationsGfx" {...buildColumnConfig(consultationsSeries)} />
					</div>
					<div className="CardWrapper --card-50">
						<h3>{Strings.dashboard.hotline}</h3>
						<Column className="hotlineGfx" {...buildColumnConfig(hotlineData)} />
					</div>
				</div>
				{viewDiagnosedDisorders && (
					<div className="DashboardGfxContainer">
						<div className="CardWrapper --card-50">
							<h3>{Strings.dashboard.disturbances}</h3>
							{diagnosisSeries.length > 0 ? (
								<Column className="disturbancesGfx" {...buildColumnConfig(diagnosisSeries)} />
							) : (
								<p>{Strings.dashboard.noData}</p>
							)}
						</div>
						<div className="CardWrapper --card-50">
							<h3>{Strings.dashboard.disturbances}</h3>
							{diagnosisSeries.length > 0 ? (
								<Pie className="disturbancesOtherGfx" {...buildPieConfig(withPercentages(diagnosisSeries))} />
							) : (
								<p>{Strings.dashboard.noData}</p>
							)}
						</div>
					</div>
				)}
			</React.Fragment>
		);
	}

	renderDrawerContent() {
		const { contracts = [] } = this.state;

		return (
			<div className="DashboardDrawer">
				<table>
					<thead>
						<tr>
							<th align="left" scope="col">
								{Strings.fields.startDate}
							</th>
							<th align="left" scope="col">
								{Strings.fields.endDate}
							</th>
							<th align="left" scope="col">
								{Strings.dashboard.purchased}
							</th>
							<th align="left" scope="col">
								{Strings.dashboard.consumed}
							</th>
							<th align="left" scope="col">
								{Strings.contactUs.description}
							</th>
						</tr>
					</thead>
					<tbody>
						{contracts.map((contract: any) => (
							<tr key={`contract_${contract._id}`}>
								<td>{DateTime.fromISO(contract.startDate).toFormat("dd-MM-yyyy")}</td>
								<td>{DateTime.fromISO(contract.endDate).toFormat("dd-MM-yyyy")}</td>
								<td data-negative={contract.value < 0} data-positive={contract.value >= 0}>
									{contract.value}
								</td>
								<td
									data-negative={contract.consultations < 0}
									data-positive={contract.consultations >= 0}
								>
									{contract.consultations}
								</td>
								<td>{contract.description || "-"}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	renderDrawer() {
		const { showDrawer } = this.state;

		return (
			<Drawer
				title={<h2 className="DrawerTitle">{Strings.dashboard.contractDetails}</h2>}
				footer={
					<div className="DrawerFooter">
						<button
							className="DrawerFooterButton --btn-primary"
							onClick={() => this.setState({ showDrawer: false })}
						>
							{Strings.generic.close}
						</button>
					</div>
				}
				className="drawer"
				width={500}
				placement="right"
				closable={true}
				visible={showDrawer}
				onClose={() => this.setState({ showDrawer: false })}
			>
				{this.renderDrawerContent()}
			</Drawer>
		);
	}

	render() {
		const { filters, business, eap, exporting } = this.state;
		const { language, mobile } = this.props;

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.sidebar.dashboard} | Team24</title>
					<meta name="description" content="Description of Dashboard" />
				</Helmet>
				<div className="DashboardContent">
					<div className="MultiTitle">
						<h1>{Strings.sidebar.dashboard}</h1>
						{!mobile && (
							<div className="DashboardTitleButtons">
								<button
									type="button"
									onClick={() => this.exportPdf()}
									disabled={exporting}
									title={Strings.dashboard.exportPdf}
									aria-label={Strings.dashboard.exportPdf}
								>
									<Icons.Print />
								</button>
								{business?.businessGroups && (
									<div className="DashboardGroupFilterWrapper">
										<Select
											id="drawer_user"
											className="DashboardGroupFilterInput"
											placeholder={Strings.coworkers.group}
											showSearch
											allowClear
											filterOption={(input: any, option: any) =>
												option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
											}
											value={filters?.group || null}
											onChange={(elem: any) =>
												this.setState(
													(state: any) => ({
														filters: {
															...state?.filters,
															group: elem
														}
													}),
													() => this.getData()
												)
											}
										>
											{business?.groups?.map((group: any) => (
												<Select.Option key={`group_${group?._id}`} value={group._id}>
													{group.name}
												</Select.Option>
											))}
										</Select>
									</div>
								)}
								<ConfigProvider locale={language === "pt" ? pt : en}>
									<RangePicker
										allowEmpty={[false, false]}
										ranges={{
											[Strings.ranges.today]: [moment(), moment()],
											[Strings.ranges.thisMonth]: [
												moment().startOf("month"),
												moment().endOf("day")
											],
											[Strings.ranges.lastMonth]: [
												moment().subtract(1, "month").startOf("month"),
												moment().subtract(1, "month").endOf("month")
											],
											[Strings.ranges.thisYear]: [
												moment().startOf("year"),
												moment().endOf("month")
											],
											[Strings.ranges.lastYear]: [
												moment().subtract(1, "year").startOf("year"),
												moment().subtract(1, "year").endOf("year")
											]
										}}
										allowClear={false}
										placeholder={[Strings.fields.startDate, Strings.fields.endDate]}
										suffixIcon={<Icons.Calendar date color />}
										disabledDate={this.disabledRange}
										separator="-"
										value={
											(filters?.startDate &&
												filters?.endDate && [
													moment(filters?.startDate, "YYYY-MM-DD"),
													moment(filters?.endDate, "YYYY-MM-DD")
												]) ||
											null
										}
										onChange={(value: any) => {
											this.setState(
												{
													filters: {
														startDate: value?.[0],
														endDate: value?.[1]
													}
												},
												() => this.getData()
											);
										}}
									/>
								</ConfigProvider>
							</div>
						)}
					</div>
					{mobile && (
						<div className="DashboardTitleButtons">
							{business?.businessGroups && (
								<div className="DashboardGroupFilterWrapper">
									<Select
										id="drawer_user"
										className="DashboardGroupFilterInput"
										placeholder={Strings.coworkers.group}
										showSearch
										filterOption={(input: any, option: any) =>
											option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}
										value={filters?.group || null}
										onChange={(elem: any) =>
											this.setState(
												(state: any) => ({
													filters: {
														...state?.filters,
														group: elem
													}
												}),
												() => this.getData()
											)
										}
									>
										{business?.groups?.map((group: any) => (
											<Select.Option key={`group_${group?._id}`} value={group._id}>
												{group.name}
											</Select.Option>
										))}
									</Select>
								</div>
							)}
							<ConfigProvider locale={language === "pt" ? pt : en}>
								<RangePicker
									allowEmpty={[false, false]}
									ranges={{
										[Strings.ranges.today]: [moment(), moment()],
										[Strings.ranges.thisMonth]: [moment().startOf("month"), moment().endOf("day")],
										[Strings.ranges.lastMonth]: [
											moment().subtract(1, "month").startOf("month"),
											moment().subtract(1, "month").endOf("month")
										],
										[Strings.ranges.thisYear]: [moment().startOf("year"), moment().endOf("month")],
										[Strings.ranges.lastYear]: [
											moment().subtract(1, "year").startOf("year"),
											moment().subtract(1, "year").endOf("year")
										]
									}}
									allowClear={false}
									placeholder={[Strings.fields.startDate, Strings.fields.endDate]}
									suffixIcon={<Icons.Calendar date color />}
									disabledDate={this.disabledRange}
									separator="-"
									value={
										(filters?.startDate &&
											filters?.endDate && [
												moment(filters?.startDate, "YYYY-MM-DD"),
												moment(filters?.endDate, "YYYY-MM-DD")
											]) ||
										null
									}
									onChange={(value: any) =>
										this.setState(
											(state: any) => ({
												filters: {
													...state?.filters,
													startDate: value?.[0],
													endDate: value?.[1]
												}
											}),
											() => this.getData()
										)
									}
								/>
							</ConfigProvider>
						</div>
					)}
					{this.renderCards()}
					{this.renderGfx()}
					<div className="EapContent">
						<div className="EapServiceCardWraper">
							{business?.serviceFinantial && (
								<div className="EapServiceCard">
									<img alt="Financial Icon" src={finantialIcon} />
									<h3>{renderString(Strings.eap.finantial)}</h3>
									<p>
										<strong>{eap?.finantial || 0}</strong>
									</p>
									<span>{Strings.eap.requests}</span>
								</div>
							)}
							{business?.serviceLegal && (
								<div className="EapServiceCard">
									<img alt="Legal Icon" src={legalIcon} />
									<h3>{renderString(Strings.eap.legal)}</h3>
									<p>
										<strong> {eap?.legal || 0}</strong>
									</p>
									<span>{Strings.eap.requests}</span>
								</div>
							)}
							{business?.serviceTax && (
								<div className="EapServiceCard">
									<img alt="Tax Icon" src={taxIcon} />
									<h3>{renderString(Strings.eap.tax)}</h3>
									<p>
										<strong>{eap?.tax || 0}</strong>
									</p>
									<span>{Strings.eap.requests}</span>
								</div>
							)}
							{business?.serviceSocial && (
								<div className="EapServiceCard">
									<img alt="Legal Icon" src={socialIcon} />
									<h3>{renderString(Strings.eap.social)}</h3>
									<p>
										<strong>{eap?.social || 0}</strong>
									</p>
									<span>{Strings.eap.requests}</span>
								</div>
							)}
							{business?.serviceNutritional && (
								<div className="EapServiceCard">
									<img alt="Legal Icon" src={nutritionalIcon} />
									<h3>{renderString(Strings.eap.nutritional)}</h3>
									<p>
										<strong>{eap?.nutritional || 0}</strong>
									</p>
									<span>Pedidos</span>
								</div>
							)}
                            {business?.serviceHealthInsurance && (
								<div className="EapServiceCard">
									<img alt="Health Insurance Icon" src={healthInsuranceIcon} />
									<h3>{renderString(Strings.eap.healthInsurance)}</h3>
									<p>
										<strong>{eap?.healthInsurance || 0}</strong>
									</p>
									<span>Pedidos</span>
								</div>
							)}
						</div>
					</div>
					{this.renderDrawer()}
				</div>
				{mobile && <Footer />}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	mobile: state.mobile
});

export default connect(mapStateToProps)(Dashboard);
